import { Col, FormGroup, Label } from 'reactstrap';
import React, { FunctionComponent, useState } from 'react';
import { object, string } from 'yup';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';

// constants
import { ApiResponse, Contract, ReactApiDataActions } from 'constants/api';

// components
import FormControl from 'components/utils/FormControl';
import Tooltip from 'components/utils/Tooltip';
import { hasSomeRole } from '../../utils/userUtils';
import { Roles } from '../../constants/literals';

// hocs
import withUserRoles from 'hocs/withUserRoles';
import ReservedForTestingCheckbox from 'components/utils/ReservedForTestingCheckbox';
import StyledCheckbox from 'components/styled/StyledCheckbox';
import FormModal from './FormModal';

interface InProps {
    exchange: string;
    contract: Contract;
}

interface EnhanceProps {
    contractUpdateRequest: ApiDataBinding<ApiResponse<Contract>>;
    apiDataActions: ReactApiDataActions;
    userRoles: Roles[];
}

type Props = EnhanceProps & InProps & WithTranslation;

const enhance = compose<Props, InProps>(
    withApiData({ contractUpdateRequest: 'putContract' }),
    withTranslation(),
    withUserRoles
);

type FormValues = {
    contract_name: string;
    announce: boolean;
    peering_db_id: string;
    peering_db_sales_email: string;
    erp_contract_identifier: string;
    reserved_for_testing: boolean;
};

const ContractEditModal: FunctionComponent<Props> = ({
    exchange,
    contract,
    contractUpdateRequest,
    apiDataActions,
    userRoles,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }

    function onSubmit(values: FormValues) {
        return contractUpdateRequest.perform(
            {
                uuid: contract.uuid,
                exchange: exchange.toUpperCase(),
            },
            {
                contract: {
                    contractname: values.contract_name,
                    erp_contract_identifier: values.erp_contract_identifier,
                    reserved_for_testing: values.reserved_for_testing,
                    announce: values.announce,
                    peering_db_id: values.peering_db_id,
                    peering_db_sales_email: values.peering_db_sales_email,
                },
            }
        );
    }

    function onSuccess() {
        apiDataActions.invalidateCache('getContract', {
            exchange: exchange.toUpperCase(),
            contractUuid: contract.uuid,
        });
    }

    return (
        <React.Fragment>
            <FormModal
                header={t('connection.addNewConnection')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={{
                    contract_name: contract.contractname || '',
                    erp_contract_identifier: contract.erp_contract_identifier || '',
                    reserved_for_testing: contract.reserved_for_testing,
                    announce: contract.announce,
                    peering_db_id: contract.peering_db_id || '',
                    peering_db_sales_email: contract.peering_db_sales_email || ''
                }}
                validationSchema={object().shape({
                    contract_name: string().required(t('validation.requiredField')),
                })}
                onSubmit={onSubmit}
                onSuccess={onSuccess}
                successMessage={t('notification.editContractSuccessful')}
            >
                {({ errors, touched, values }) => (
                    <React.Fragment>
                        {
                            <>
                                <FormGroup row>
                                    <Col md="3">
                                        <Label htmlFor="contract_name">{t('editContractModal.contractName')}</Label>
                                    </Col>
                                    <Col md="9">
                                        <FormControl
                                            type="text"
                                            name="contract_name"
                                            id="contract_name"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row>
                                    <Col md="3">
                                        <Label htmlFor="erp_contract_identifier">
                                            {t('editContractModal.erpContractId')}
                                        </Label>
                                    </Col>
                                    <Col md="9">
                                        <FormControl
                                            type="text"
                                            name="erp_contract_identifier"
                                            id="erp_contract_identifier"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row>
                                    <Col md="3">
                                        <Label htmlFor="peering_db_id">{t('contract.peeringDbId')}</Label>
                                    </Col>
                                    <Col md="9">
                                        <FormControl
                                            type="text"
                                            name="peering_db_id"
                                            id="peering_db_id"
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </Col>
                                </FormGroup>

                                <FormGroup row>
                                    <Col md="3">
                                        <Label htmlFor="peering_db_sales_email">
                                            {t('contract.peeringDbSalesEmail')}
                                        </Label>
                                    </Col>
                                    <Col md="9">
                                        <FormControl
                                            type="email"
                                            name="peering_db_sales_email"
                                            id="peering_db_sales_email"
                                            errors={errors}
                                            touched={touched}
                                        />

                                        <p className="mb-0 mt-1">
                                            {t('editContractModal.modifyPeeringDbSalesEmailNote')}
                                        </p>
                                    </Col>
                                </FormGroup>

                                <FormGroup row>
                                    <Col md={3}>
                                        <Tooltip
                                            triggerBtn={
                                                <Label htmlFor="announce" className="mb-0">
                                                    <span className="mr-2">
                                                        {t('contract.showOnConnectedNetworks')}
                                                    </span>
                                                    <i className="fa fa-question-circle" />
                                                </Label>
                                            }
                                        >
                                            {t('contract.showOnConnectedNetworksDescription')}
                                        </Tooltip>
                                    </Col>
                                    <Col md={9} className="d-flex align-items-center">
                                        <StyledCheckbox name="announce" checked={values.announce} />
                                    </Col>
                                </FormGroup>

                                <ReservedForTestingCheckbox checked={values.reserved_for_testing} />
                            </>
                        }
                    </React.Fragment>
                )}
            </FormModal>
            <React.Fragment>
                {hasSomeRole(userRoles, Roles.admin, Roles.noc, Roles.ixaas_client) && (
                    <Tooltip
                        data-test="edit-contract-btn"
                        triggerBtn={
                            <i
                                className="fas fa-edit fa-pull-right mt-1 text-primary cursor-pointer"
                                onClick={toggle}
                            />
                        }
                    >
                        {t('editContractModal.headerTitle')}
                    </Tooltip>
                )}
            </React.Fragment>
        </React.Fragment>
    );
};

export default enhance(ContractEditModal);
